import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import { selectAIMessages } from '@store/reducers/ai/selectors'
import type { AiRemainingRequestsResponse } from '@store/reducers/ai/types'
import { artBoardActions } from '@store/reducers/artboard'
import { saveProjectSaga } from '@store/reducers/manager/sagas'
import type { AxiosResponse } from 'axios'
import type { SagaIterator } from 'redux-saga'
import { call, put, select } from 'redux-saga/effects'

import { AIExternalActions, AIInternalActions } from '../../actions'
import { type ChatHistoryMessage, fetchHistory } from '../../v2/chatAgentClient'
import { getAiRemainingRequests } from '../startChat'

/**
 * v2 chat opening: load the persisted (project, user) transcript from the
 * chat-agent's GET /chat/history so history survives reloads (plan 06 §6);
 * an empty/failed load shows the greeting. The counter is seeded from the
 * legacy `/remaining` endpoint (the chat-agent shares the same limit tables),
 * so the "Actions limit reached" banner and the remainingRequests === 0 send
 * gate work right after a page reload — `done` events then keep the counter
 * fresh during the session.
 */
export function* startChatV2({
  payload,
}: ReturnType<typeof AIExternalActions.startChat>): SagaIterator {
  const { message, keepSidebarClosed = false } = payload

  let projectId = yield select(migrationProjectSelector.getProjectId)

  // `keepSidebarClosed` leaves the current state alone instead of forcing the
  // panel closed, so a chat the user opened by hand stays open.
  if (!keepSidebarClosed) {
    yield put(
      artBoardActions.showAiChat({
        showAiChat: true,
      })
    )
  }

  // The chat agent requires a projectId on every /chat request, so an
  // unsaved project has to be persisted before the first message. Saved in
  // the background with `ignoreAuthenticated` so opening the chat never
  // prompts to log in / register. Skipped when a message is passed in
  // (generate flow), which already triggers the save itself.
  if (!projectId && !message) {
    yield call(saveProjectSaga, { payload: { ignoreAuthenticated: true } })
    projectId = yield select(migrationProjectSelector.getProjectId)
  }

  if (message) {
    yield put(
      AIExternalActions.addMessage({
        message: {
          type: 'user',
          text: message,
          isDescription: true,
        },
      })
    )
  } else {
    const messages: ReturnType<typeof selectAIMessages> =
      yield select(selectAIMessages)

    if (messages.length === 0) {
      // Load the persisted (project, user) transcript so history survives page
      // reloads (plan 06 §6); fall back to the greeting when there is none or
      // the request fails. projectId is required by the endpoint. The skeleton
      // (OCT-4580) covers the fetch, same as the v1 flow.
      let history: ChatHistoryMessage[] = []
      if (projectId) {
        yield put(AIInternalActions.setHistoryLoading(true))

        try {
          history = yield call(fetchHistory, projectId)
        } catch {
          // ignore — show the greeting instead
        } finally {
          yield put(AIInternalActions.setHistoryLoading(false))
        }
      }

      if (history.length > 0) {
        yield put(
          AIInternalActions.setMessages(
            history.map((m) => ({ type: m.role, text: m.content }))
          )
        )
      } else {
        yield put(
          AIExternalActions.addMessage({
            message: {
              type: 'assistant',
              text: `Hi there! I'm your friendly AI assistant, currently in Beta version and still learning. How can I help you with your project?`,
            },
          })
        )
      }
    }
  }

  if (projectId) {
    try {
      const response: AxiosResponse<AiRemainingRequestsResponse> = yield call(
        getAiRemainingRequests,
        projectId
      )

      yield put(
        AIInternalActions.updateRemainingRequests(
          response.data.remainingRequests
        )
      )
    } catch {
      // Keep the counter unseeded if the legacy endpoint fails — `done`
      // events will fill it in after the first turn.
    }
  }
}
